/**
 * Lock Messaging Utility
 *
 * Maps CaseFile lock reasons to user-friendly messages, descriptions,
 * and resolution CTAs for use throughout the frontend.
 *
 * Lock reasons per ACL v2.3:
 * - pending_patient_authorization: CaseFile awaits patient consent
 * - pending_adult_transition: Care recipient turned 18, reauthorization needed
 * - authorization_revoked: Patient/guardian revoked authorization
 */

import type { CaseFileLockReason } from '../types/settings';

export interface LockMessage {
  title: string;
  description: string;
  actionLabel: string;
  /** Route path to resolve the lock. null means no in-app resolution. */
  actionRoute: string | null;
  /** Short inline message for tooltips and disabled button explanations */
  inlineMessage: string;
}

const LOCK_MESSAGES: Record<CaseFileLockReason, LockMessage> = {
  pending_patient_authorization: {
    title: 'CaseFile Locked — Authorization Required',
    description:
      'This CaseFile is locked because patient authorization has not been completed. ' +
      'Documents, care team management, and editing are disabled until the patient authorizes access.',
    actionLabel: 'Complete Authorization',
    actionRoute: null, // Resolved via inline banner options (in-app sign / upload)
    inlineMessage: 'Complete patient authorization to enable this action.',
  },
  pending_adult_transition: {
    title: 'CaseFile Locked — Reauthorization Needed',
    description:
      'The care recipient has turned 18. Reauthorization is required under HIPAA to continue ' +
      'managing this CaseFile. Documents and care team management are disabled until reauthorization is complete.',
    actionLabel: 'Start Reauthorization',
    actionRoute: null, // Future: route to adult transition flow
    inlineMessage: 'Complete adult reauthorization to enable this action.',
  },
  authorization_revoked: {
    title: 'CaseFile Locked — Authorization Revoked',
    description:
      'Authorization for this CaseFile has been revoked by the care recipient or their representative. ' +
      'Contact them to re-establish authorization, or reach out to support for assistance.',
    actionLabel: 'Contact Support',
    actionRoute: null, // Future: route to support/contact flow
    inlineMessage: 'Authorization has been revoked. Contact the care recipient to restore access.',
  },
};

const FALLBACK_LOCK_MESSAGE: LockMessage = {
  title: 'CaseFile Locked',
  description: 'This CaseFile is currently locked. Some actions are unavailable until it is unlocked.',
  actionLabel: 'Learn More',
  actionRoute: null,
  inlineMessage: 'This CaseFile is locked.',
};

/**
 * Get the full lock message config for a given lock reason.
 */
export function getLockMessage(reason?: CaseFileLockReason | string): LockMessage {
  if (!reason) return FALLBACK_LOCK_MESSAGE;
  return LOCK_MESSAGES[reason as CaseFileLockReason] ?? FALLBACK_LOCK_MESSAGE;
}

/**
 * Get a short inline explanation for why an action is blocked.
 */
export function getLockInlineMessage(reason?: CaseFileLockReason | string): string {
  return getLockMessage(reason).inlineMessage;
}
